
import React, { useEffect, useState, useRef } from "react";
import Flexbox from "flexbox-react";
import { inject, observer } from "mobx-react";
import { FormGroup, NumericInput, InputGroup, Tooltip, Position, Switch, Button } from "@blueprintjs/core";
import axios from "axios";
import Cookies from "js-cookie";
import "react-datepicker/dist/react-datepicker.css";
import { Classes } from "@blueprintjs/core";
import "./GoalSettingsCreateForm.css";
import PageHeading from "./PageHeading";
import { FilledButton, OutlinedButton } from "../buttons-20250224T111359Z-001/buttons";
import { Icon } from "@blueprintjs/core";
import AssesementQuestionDrawer from "./AssesementQuestionDrawer";
import ManagerQuestionDrawer from "./ManagerQuestionDrawer";
import RatingLabelDialog from './RatingLabelDialog';
import { Divider } from "@blueprintjs/core";
import GoalProgressStatusManager from "./GoalProgressStatusManager";
import ToastStore from "../../stores/ToastStore";
import MultilevelSettingDialog from "../goal_cycle/MultilevelSettingDialog";
import EditIcon from "../../assets/editIcon.svg";
import StepProcessDrawer from "../goal_cycle/StepProcessDrawer";
import ScrollableDropdown from "../shared/ScrollableDropdown";
import { REVIEW_PERIOD, REVIEW_PERIOD_LABELS, REVIEW_PERIOD_FREQUENCY } from "../../static/Constant";
import CheckInFrequencySettingDialog from "../goal_cycle/CheckInFrequencySettingDialog";

const GoalSettingsCreateForm = inject("ToastStore")(
  observer(({ goalData, onClose, getAllGoals, ...props }) => {
    const statusRef = useRef();
    const [goalSubmitLoading, setGoalSubmitLoading] = useState(false);
    const [title, setTitle] = useState(goalData?.title || "");
    const [description, setDescription] = useState(goalData?.description || "");
    const [reviewPeriod, setReviewPeriod] = useState(goalData?.review_period ?? REVIEW_PERIOD.MONTHLY);
    const [reviewPeriodFrequency, setReviewPeriodFrequency] = useState(goalData?.review_period_frequency ?? 1);
    const [checkinFrequency, setCheckinFrequency] = useState(goalData?.checkins_period_frequency ?? "");

    const [status, setStatus] = useState(goalData?.status || false);
    const [goalApproval, setGoalApproval] = useState(goalData?.goal_approval == 1 ? true : false);
    const [globalReviewSteps, setGlobalReviewSteps] = useState(goalData?.global_review_level_steps || []);
    const [isEditable, setIsEditable] = useState(goalData?.is_editable == 1 ? true : false);
    const [rating, setRating] = useState(goalData?.rating == 1 ? true : false);
    const [startRatingValue, setStartRatingValue] = useState(goalData?.start_rating_value ?? "");
    const [endRatingValue, setEndRatingValue] = useState(goalData?.end_rating_value ?? "");
    const [weight, setWeight] = useState(goalData?.weight == 1 ? true : false);
    const [isAnswerAllowOnQuestion, setIsAnswerAllowOnQuestion] = useState(goalData?.id
        ? goalData.is_answer_allow_on_question == 1: true);
    const [selfAssessment, setSelfAssessment] = useState(goalData?.self_assessment == 1 ? true : false);
    const [isStepProcess, setIsStepProcess] = useState(goalData?.is_step_process == 1 ? true : false);
    const [isRatingEnabledQuestions, setIsRatingEnabledQuestions] = useState(goalData?.is_assessment_question_rating == 1 ? true : false);
    const [isMultiLevelReview, setIsMultiLevelReview] = useState(goalData?.is_review_level == 1 ? true : false);
    const [isCheckinEnabled, setIsCheckinEnabled] = useState(goalData?.is_checkin_enable == 1 ? true : false);
    const [goalProgressStatus, setGoalProgressStatus] = useState(true);
    const [isRatingLabelDialogOpen, setIsRatingLabelDialogOpen] = useState(false);
    const [isMultiLevelReviewDialogOpen, setIsMultiLevelReviewDialogOpen] = useState(false);
    const [isMultilevelSettingDialogOpen, setIsMultilevelSettingDialogOpen] = useState(false);
    const [isStepProcessDrawerOpen, setIsStepProcessDrawerOpen] = useState(false);
    const [isCheckinDialogOpen, setIsCheckinDialogOpen] = useState(false);
    const [ratingMethod, setRatingMethod] = useState(goalData?.rating_method || 0);
    const [ratingLabels, setRatingLabels] = useState(goalData?.rating_labels || []);
    const [isAssesementDrawerOpen, setIsAssesementDrawerOpen] = useState(false);
    const [isManagerDrawerOpen, setIsManagerDrawerOpen] = useState(false);
    const [assessmentSettingId, setAssesementSettingId] = useState(goalData?.assessment_setting_id || "");
    const [goalCreated, setGoalCreated] = useState(!!goalData?.id);
    const [goalDataId, setGoalDataId] = useState(goalData?.id || "");
    const [error, setError] = useState("");

    // Helper function to get current goal data
    const getCurrentGoalData = () => {
      // If goalData has an id, return it directly
      if (goalData?.id) {
        return goalData;
      }
      // If we have a goalDataId but no goalData.id, merge them
      if (goalDataId && goalData) {
        return { ...goalData, id: goalDataId };
      }
      // If we only have goalDataId, create a minimal object
      if (goalDataId) {
        return { id: goalDataId };
      }
      // Fallback to original goalData
      return goalData;
    };

    // Memoized current goal data to avoid recalculating on every render
    const currentGoalData = getCurrentGoalData();
    const handleInputChange = e => {
      if (e.target.name === "title") {
        const titleValue = e.target.value;
        setTitle(titleValue);
      }
      if (e.target.name === "description") {
        const descValue = e.target.value;
        setDescription(descValue);
      }
      if (e.target.name === "status") {
        const statusValue = e.target.value;
        setStatus(statusValue);
      }
      if (e.target.name === "StartRatingValue") {
        const startValue = e.target.value;
        setStartRatingValue(startValue);
      }
      if (e.target.name === "EndRatingValue") {
        const endValue = e.target.value;
        setEndRatingValue(endValue);
      }
    };

    const iconPlaceholder = <div style={{ width: "16px", height: "16px" }} />;

    const validateForm = () => {
      if (!title.trim()) {
        ToastStore.showToast("Title is required.", "warning");
        return false;
      }
      if (
        reviewPeriod === null ||
        reviewPeriod === undefined ||
        reviewPeriodFrequency === null ||
        reviewPeriodFrequency === undefined
      ) {
        ToastStore.showToast(
          "Review Period and Review Frequency are required.",
          "warning"
        );
        return false;
      }
    
      return true;
    };

    const handleSubmit = (e, isPublished, closeForm = true, updateGoalData = false, newSelfAssessment, newStepProcess) => {
      e.preventDefault();
      if (!validateForm()) return;
      setStatus(isPublished);
      const effectiveSelfAssessment = newSelfAssessment !== undefined ? newSelfAssessment : selfAssessment;
      const effectiveStepProcess = newStepProcess !== undefined ? newStepProcess : isStepProcess;
      let payload = {};
      const statusList = statusRef.current?.getStatusList();
      setGoalSubmitLoading(true);
      if (currentGoalData?.id) {
        if (currentGoalData.title !== title) payload.title = title;
        if (currentGoalData.description !== description) payload.description = description;
        if (currentGoalData.review_period !== reviewPeriod) payload.review_period = reviewPeriod;
        if (currentGoalData.review_period_frequency !== reviewPeriodFrequency) payload.review_period_frequency = reviewPeriodFrequency;
        if (currentGoalData.status !== isPublished) payload.status = isPublished ? 1 : 0;
        if (currentGoalData.self_assessment !== (effectiveSelfAssessment ? 1 : 0)) payload.self_assessment = effectiveSelfAssessment ? 1 : 0;
        if (currentGoalData.is_assessment_question_rating !== (isRatingEnabledQuestions ? 1 : 0)) payload.is_assessment_question_rating = isRatingEnabledQuestions ? 1 : 0;
        if (currentGoalData.rating !== (rating ? 1 : 0)) payload.rating = rating ? 1 : 0;
        if (currentGoalData.is_goal_progress_status !== (goalProgressStatus ? 1 : 0)) payload.is_goal_progress_status = goalProgressStatus ? 1 : 0;
        if (currentGoalData.weight !== (weight ? 1 : 0)) payload.weight = weight ? 1 : 0;
        if (currentGoalData.is_answer_allow_on_question !== (isAnswerAllowOnQuestion ? 1 : 0)) payload.is_answer_allow_on_question = isAnswerAllowOnQuestion ? 1 : 0;
        if (currentGoalData.is_review_level !== (isMultiLevelReview ? 1 : 0)) payload.is_review_level = isMultiLevelReview ? 1 : 0;
        if (currentGoalData.is_step_process !== (effectiveStepProcess ? 1 : 0)) {payload.is_step_process = effectiveStepProcess ? 1 : 0;}
        if (currentGoalData.is_checkin_enable !== (isCheckinEnabled ? 1 : 0)) payload.is_checkin_enable = isCheckinEnabled ? 1 : 0;
        if (currentGoalData.goal_approval !== (goalApproval ? 1 : 0)) payload.goal_approval = goalApproval ? 1 : 0;
        if (currentGoalData.is_editable !== (isEditable ? 1 : 0)) payload.is_editable = isEditable ? 1 : 0;
        if (rating) {
          if (currentGoalData.start_rating_value !== startRatingValue) payload.start_rating_value = startRatingValue;
          if (currentGoalData.end_rating_value !== endRatingValue) payload.end_rating_value = endRatingValue;
        }
        if (isMultiLevelReview) {
          payload.global_review_level_steps = globalReviewSteps;
          payload.rating_method = ratingMethod;
        }
        if (goalProgressStatus) {
          payload.progress_statuses = statusList;
        }
        if (rating && startRatingValue !== "" && endRatingValue !== "" && ratingLabels.length > 0) {
          payload.rating_labels = ratingLabels;
        }
        if (isCheckinEnabled) {
          payload.checkins_period_frequency = checkinFrequency;
        }
      } else {
        // First-time form submission (send all fields)
        payload = {
          title,
          description,
          review_period: reviewPeriod,
          review_period_frequency: reviewPeriodFrequency,
          status: isPublished ? 1 : 0,
          self_assessment: effectiveSelfAssessment ? 1 : 0,
          rating: rating ? 1 : 0,
          is_assessment_question_rating: isRatingEnabledQuestions ? 1 : 0,
          is_goal_progress_status: goalProgressStatus ? 1 : 0,
          goal_approval: goalApproval ? 1 : 0,
          is_editable: isEditable ? 1 : 0,
          weight: weight ? 1 : 0,
          is_answer_allow_on_question: isAnswerAllowOnQuestion ? 1 : 0,
          is_review_level: isMultiLevelReview ? 1 : 0,
          is_step_process: effectiveStepProcess ? 1 : 0,
          is_checkin_enable: isCheckinEnabled ? 1 : 0,
        };

        if (rating) {
          payload.start_rating_value = startRatingValue;
          payload.end_rating_value = endRatingValue;
        }
        if (rating && startRatingValue !== "" && endRatingValue !== "" && ratingLabels.length > 0) {
          payload.rating_labels = ratingLabels;
        }
        if (isMultiLevelReview) {
          payload.rating_method = ratingMethod;
          payload.global_review_level_steps = globalReviewSteps;
        }
        if (!goalDataId && goalProgressStatus && statusList.length > 0) {
          payload.progress_statuses = statusList;
        }
        if (selfAssessment) {
          payload.is_assessment_question_rating = isRatingEnabledQuestions ? 1 : 0;
        }
        if (isCheckinEnabled) {
          payload.checkins_period_frequency = checkinFrequency;
        }
      }

      // Prevent sending empty updates
      if (currentGoalData?.id && Object.keys(payload).length === 0 && !closeForm) {
        ToastStore.showToast("No changes detected.", "warning");
        return;
      }
      // Optimized API URL and method determination
      const goalId = currentGoalData?.id || goalDataId;
      const isUpdate = currentGoalData?.id || goalCreated;
      const apiUrl = isUpdate
        ? `${Cookies.get("apiEnv")}/goal_settings/${goalId}`
        : `${Cookies.get("apiEnv")}/goal_settings`;
      const method = isUpdate ? "put" : "post";

      axios({ method, url: apiUrl, data: payload })
        .then(response => {
          const responseData = response.data;

          // Update local state if this is a new goal
          if (!goalCreated && responseData?.id) {
            setGoalCreated(true);
            setGoalDataId(responseData.id);

            // Update parent's goalData if needed
            if (updateGoalData && responseData && props.onGoalDataUpdate) {
              const updatedGoalData = { ...goalData, ...responseData };
              props.onGoalDataUpdate(updatedGoalData);
            }
          }
          // Handle assessment setting ID
          if (updateGoalData && responseData?.assessment_setting_id) {
            setAssesementSettingId(responseData.assessment_setting_id);
          }

          // Close form and refresh goals if needed
          if (closeForm) {
            onClose();
            getAllGoals();
          }
          setGoalSubmitLoading(false);
        })
        .catch(error => {
          // const errorLogPayload = {
          // error_message: error, 
          // request_payload: payload,
          // goal_id: goalData?.id || goalDataId || null, 
          // };
          // axios.post(`${Cookies.get("apiEnv")}/goal_settings/send_error_email`,errorLogPayload)
          setError("Failed to save goal. Please try again.");
          setGoalSubmitLoading(false);

        });
    };

    const handleStepProcessToggle = (event) => {
      const newValue = event.target.checked;
      if (newValue) {
        if (!title?.trim()) {
          ToastStore.showToast("Title is required.", "warning");
          return;
        }
        if (
          reviewPeriod === null ||
          reviewPeriod === undefined ||
          reviewPeriodFrequency === null ||
          reviewPeriodFrequency === undefined
        ) {
          ToastStore.showToast(
            "Review Period and Review Frequency are required.",
            "warning"
          );
          return;
        }
      
        if (!rating) {
          ToastStore.showToast("Enable Rating is required.", "warning");
          return;
        }
      }
      setIsStepProcess(newValue);
      if (newValue) {
        const isPublished = goalDataId ? status : false;
        handleSubmit(
          { preventDefault: () => { } },
          isPublished,
          false,
          true,
          undefined,
          newValue
        );
        setTimeout(() => {
          setIsStepProcessDrawerOpen(true);
        }, 200);
      }
    };
    const handleManageAssessment = () => {
      setTimeout(() => {
        setIsAssesementDrawerOpen(true);
      }, 200);
    };
    const handleManageRating = () => {
      setTimeout(() => {
        setIsRatingLabelDialogOpen(true);
      }, 200);
    };
    const handleManageMultiLevelReview = () => {
      setTimeout(() => {
        setIsMultilevelSettingDialogOpen(true);
      }, 200);
    };
    const handleManageCheckin = () => {
      setTimeout(() => {
        setIsCheckinDialogOpen(true);
      }, 200);
    };
    const handleManageStepProcess = () => {
      setIsStepProcessDrawerOpen(true);
    };
    return (
      <Flexbox flexDirection="column" flexGrow={1} marginBottom="20px" style={{ width: "70%" }} marginRight="30px">
        <form onSubmit={e => e.preventDefault()}>
          <Flexbox alignItems="flex-start" className="page-header" width="100%">
            <Icon icon="arrow-left" onClick={onClose} className="arrow-icon"></Icon>

            <PageHeading
              text="Add Goal Cycle"
              description="Create a new goal cycle and align goals, timelines, and reviews across the organization."
            />
          </Flexbox>

          <Flexbox alignItems="center" justifyContent="flex-start" className="page-header" width="100%">
            {/* <img src={subMenu} /> */}
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="18" viewBox="0 0 22 18" fill="none">
              <path d="M1 6.55556L3.77778 3.77778L1 1" stroke="#3B5998" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M1 16.5556L3.77778 13.7778L1 11" stroke="#3B5998" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.77777 2.11108H21" stroke="#3B5998" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.77777 8.77783H21" stroke="#3B5998" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.77777 15.4445H21" stroke="#3B5998" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            <p className="sub-heading" style={{ marginTop: "-3px" }}>
              Add Goal Details
            </p>
          </Flexbox>
          <Divider className="divider-line push-20-b"></Divider>
          <FormGroup className="input-label" label="Title" labelFor="">
            <InputGroup
              id="Title"
              placeholder="H1 cycle"
              className=" bp5-large input-box"
              type="text"
              name="title"
              onChange={handleInputChange}
              value={title}
              style={{ height: "36px", boxShadow: "none" }}
            />
          </FormGroup>
          <FormGroup label="Description" className="input-label">
            <textarea
              className="bp3-input max-width"
              autoComplete="off"
              rows={4}
              name="description"
              onChange={handleInputChange}
              value={description}
              placeholder={"Description regarding this quarter"}
              style={{ resize: "none", boxShadow: "none", border: "1px solid #d6d6d6" }}
            />
          </FormGroup>

          <FormGroup label="Goal Cycle Duration settings" className="input-label">
            <Flexbox style={{ gap: "10px" }}>
              <ScrollableDropdown
                width={250}
                label={REVIEW_PERIOD_LABELS[reviewPeriod] || "Review Period"}
                items={Object.values(REVIEW_PERIOD).map(period => ({
                  value: period,
                  label: REVIEW_PERIOD_LABELS[period],
                }))}
                getKey={g => g.value}
                getLabel={g => g.label}
                enableSearch
                onSelect={(item) => {
                  if (checkinFrequency !== "") {
                    ToastStore.showToast(
                      "Goal cycle has changed. Please configure the check-in frequency again.",
                      "warning"
                    );
                    setCheckinFrequency("");
                  }               
                  setReviewPeriod(item.value);
                  setReviewPeriodFrequency(1);
                }}
               
              />
              <ScrollableDropdown
                width={250}
                label={
                  REVIEW_PERIOD_FREQUENCY[reviewPeriod]?.find(
                    item => item.value === reviewPeriodFrequency
                  )?.label || "Review Frequency"
                }
                items={REVIEW_PERIOD_FREQUENCY[reviewPeriod] || []}
                getKey={g => g.value}
                getLabel={g => g.label}
                enableSearch
                onSelect={(item) => {
                  if (
                    checkinFrequency !== "" &&
                    item.value !== reviewPeriodFrequency
                  ) {
                    ToastStore.showToast(
                      "Goal cycle has changed. Please configure the check-in frequency again.",
                      "warning"
                    );
                    setCheckinFrequency("");
                  }
                
                  setReviewPeriodFrequency(item.value);
                }}
              />

            </Flexbox>

          </FormGroup>

          {/* <PageSubheading subheading={"Goal Cycle configurations"}></PageSubheading> */}
          <Flexbox alignItems="center" justifyContent="flex-start" className="page-header " style={{ marginTop: "40px" }} width="100%">
            {/* <img src={subMenu} /> */}
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="18" viewBox="0 0 22 18" fill="none">
              <path d="M1 6.55556L3.77778 3.77778L1 1" stroke="#3B5998" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M1 16.5556L3.77778 13.7778L1 11" stroke="#3B5998" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M8.77777 2.11108H21" stroke="#3B5998" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M8.77777 8.77783H21" stroke="#3B5998" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M8.77777 15.4445H21" stroke="#3B5998" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>

            <p className="sub-heading" style={{ marginTop: "-3px" }}>
              Goal Cycle Configurations
            </p>
          </Flexbox>

          <Flexbox flexDirection="column" className="bp3-form-group" style={{ gap: "8px" }}>
            {[
              {
                label: "Manager Approval Required",
                subLabel: "Require managers to review and approve employee goals before they are published to ensure proper validation",
                state: goalApproval,
                setter: setGoalApproval
              },
              {
                label: "Allow Goal Editing",
                subLabel: "It allows employees to add and update their goals during the cycle,while keeping goals locked from editing when this setting is turned off.",
                state: isEditable,
                setter: setIsEditable
              },
              {
                label: "Allow Goal Weightage",
                subLabel: "Introduces weightage-based evaluation across goals, assessments, and review questions",
                state: weight,
                setter: setWeight
              },
              {
                label: "Allow Answer on Question",
                subLabel: "Allows users to provide answers directly on questions when enabled.",
                state: isAnswerAllowOnQuestion,
                setter: setIsAnswerAllowOnQuestion
              }
            ].map(({ label, subLabel, state, setter }) => (
              <div key={label} className="push-10-b">
                <Divider className="divider-line push-10-b" />
                <Flexbox justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
                  <div style={{ flex: 1 }}>
                    <div className="bp3-text-large " style={{ fontWeight: "500", fontSize: "14px" }}>
                      {label}
                    </div>
                    <div className="bp3-text-muted" style={{ fontSize: "14px", marginTop: "3px" }}>
                      {subLabel}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Switch checked={state} onChange={() => setter(!state)} />
                    {iconPlaceholder}
                  </div>
                </Flexbox>
              </div>
            ))}

            <div>
              <Divider className="divider-line push-20-b" />
              <Flexbox justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
                <div style={{ flex: 1 }}>
                  <div className="bp3-text-large " style={{ fontWeight: "500", fontSize: "14px" }}>
                    Include Rating
                  </div>
                  <div className="bp3-text-muted" style={{ fontSize: "14px", marginTop: "3px" }}>
                    Introduces a rating system as part of the evaluation process for goals, assessments, and reviews, providing measurable scoring.
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "1px" }}>
                  <Switch checked={rating} onChange={() => setRating(!rating)} />
                  {rating ? (
                    <Tooltip portalContainer={document.body}
                      content="Manage rating definitions"
                      position={Position.RIGHT}
                      hoverOpenDelay={100}
                      boundary="viewport"
                      className="custom-bp-tooltip"
                    >
                      <div
                        className="edit-icon"
                        onClick={handleManageRating}
                        style={{ cursor: "pointer" }}

                      >
                        <svg
                          width="21"
                          height="21"
                          viewBox="0 0 24 24"
                          fill="none"
                          strokeWidth="1.5"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            className="edit-icon-path"
                            strokeWidth="1.5"
                            d="M7.63704 21.2615H2.51904L2.69554 16.1885L16.1595 3.1285C16.4168 2.87888 16.761 2.73903 17.1194 2.73847C17.4779 2.73791 17.8225 2.87669 18.0805 3.1255L21.059 5.9975C21.1916 6.12538 21.2973 6.27848 21.3698 6.44778C21.4424 6.61708 21.4803 6.79918 21.4815 6.98337C21.4827 7.16756 21.447 7.35012 21.3766 7.52033C21.3062 7.69053 21.2025 7.84495 21.0715 7.9745L7.63704 21.2615ZM3.47254 20.3385H7.25854L20.4235 7.3195C20.4672 7.27634 20.5018 7.22488 20.5253 7.16815C20.5488 7.11142 20.5607 7.05056 20.5603 6.98916C20.56 6.92776 20.5473 6.86705 20.5231 6.81061C20.499 6.75416 20.4637 6.70313 20.4195 6.6605L17.441 3.7885C17.3552 3.70538 17.2403 3.65891 17.1208 3.65891C17.0013 3.65891 16.8864 3.70538 16.8005 3.7885L3.60054 16.5885L3.47254 20.3385Z"
                            fill="#707070"
                          />
                          <path
                            className="edit-icon-path"
                            strokeWidth="1.5"
                            d="M18.8589 9.5245L14.4719 5.21L15.2029 4.491L19.5899 8.8055L18.8589 9.5245Z"
                            fill="#707070"
                          />
                        </svg>
                      </div>
                    </Tooltip>
                    // <Icon icon="cog" onClick={handleManageRating} style={{ cursor: "pointer", paddingBottom: "3px" }} />
                  ) : (
                    iconPlaceholder
                  )}
                </div>
              </Flexbox>
            </div>

            {rating ? (
              <Flexbox flexDirection="row" className="bp3-form-group" style={{ gap: "5px" }}>
                <FormGroup label="Starting Goal Rating">
                  <NumericInput
                    allowNumericCharactersOnly={true}
                    name="StartRatingValue"
                    placeholder="Start Rating"
                    // min={0}
                    max={20}
                    value={startRatingValue}
                    onValueChange={val => setStartRatingValue(val)}
                  // style={{width:'50%'}}
                  />
                </FormGroup>
                <FormGroup label="Ending Goal Rating">
                  <NumericInput
                    allowNumericCharactersOnly={true}
                    name="EndRatingValue"
                    placeholder="End Rating"
                    min={startRatingValue}
                    max={100}
                    value={endRatingValue}
                    onValueChange={val => setEndRatingValue(val)}
                  // style={{width:'100%'}}
                  />
                </FormGroup>
              </Flexbox>
            ) : null}

            <div>
              <Divider className="divider-line push-20-b" />
              <Flexbox justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
                <div style={{ flex: 1 }}>
                  <div className="bp3-text-large " style={{ fontWeight: "500", fontSize: "14px" }}>
                    Review Process
                  </div>
                  <div
                    className="bp3-text-muted"
                    style={{ fontSize: "14px", marginTop: "3px" }}
                  >
                    Make the review process step-by-step, requiring users to complete each review stage in sequence.
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "1px" }}>
                  <Switch checked={isStepProcess} onChange={(event) => handleStepProcessToggle(event)} />
                  {isStepProcess ? (
                    <Tooltip portalContainer={document.body}
                      content="Manage review process"
                      position={Position.RIGHT}
                      hoverOpenDelay={100}
                      boundary="viewport"
                      className="custom-bp-tooltip"
                    >
                      <div
                        className="edit-icon"
                        onClick={handleManageStepProcess}
                        style={{ cursor: "pointer" }}

                      >
                        <svg
                          width="21"
                          height="21"
                          viewBox="0 0 24 24"
                          fill="none"
                          strokeWidth="1.5"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            className="edit-icon-path"
                            strokeWidth="1.5"
                            d="M7.63704 21.2615H2.51904L2.69554 16.1885L16.1595 3.1285C16.4168 2.87888 16.761 2.73903 17.1194 2.73847C17.4779 2.73791 17.8225 2.87669 18.0805 3.1255L21.059 5.9975C21.1916 6.12538 21.2973 6.27848 21.3698 6.44778C21.4424 6.61708 21.4803 6.79918 21.4815 6.98337C21.4827 7.16756 21.447 7.35012 21.3766 7.52033C21.3062 7.69053 21.2025 7.84495 21.0715 7.9745L7.63704 21.2615ZM3.47254 20.3385H7.25854L20.4235 7.3195C20.4672 7.27634 20.5018 7.22488 20.5253 7.16815C20.5488 7.11142 20.5607 7.05056 20.5603 6.98916C20.56 6.92776 20.5473 6.86705 20.5231 6.81061C20.499 6.75416 20.4637 6.70313 20.4195 6.6605L17.441 3.7885C17.3552 3.70538 17.2403 3.65891 17.1208 3.65891C17.0013 3.65891 16.8864 3.70538 16.8005 3.7885L3.60054 16.5885L3.47254 20.3385Z"
                            fill="#707070"
                          />
                          <path
                            className="edit-icon-path"
                            strokeWidth="1.5"
                            d="M18.8589 9.5245L14.4719 5.21L15.2029 4.491L19.5899 8.8055L18.8589 9.5245Z"
                            fill="#707070"
                          />
                        </svg>
                      </div>
                    </Tooltip>
                  ) : (
                    iconPlaceholder
                  )}
                </div>
              </Flexbox>
            </div>

            <div>
              <Divider className="divider-line push-20-b" />
              <Flexbox justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
                <div style={{ flex: 1 }}>
                  <div className="bp3-text-large " style={{ fontWeight: "500", fontSize: "14px" }}>
                    Rating Calculation Method
                  </div>
                  <div className="bp3-text-muted" style={{ fontSize: "14px", marginTop: "3px" }}>Defines how ratings are calculated across goals, assessments, and review evaluations.
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "1px" }}>
                  <Switch checked={isMultiLevelReview} onChange={() => setIsMultiLevelReview(!isMultiLevelReview)} />
                  {isMultiLevelReview ? (
                    <div
                      className="edit-icon"
                      onClick={handleManageMultiLevelReview}
                      style={{ cursor: "pointer" }}
                    >
                      <svg
                        width="21"
                        height="21"
                        viewBox="0 0 24 24"
                        fill="none"
                        strokeWidth="1.5"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          className="edit-icon-path"
                          strokeWidth="1.5"
                          d="M7.63704 21.2615H2.51904L2.69554 16.1885L16.1595 3.1285C16.4168 2.87888 16.761 2.73903 17.1194 2.73847C17.4779 2.73791 17.8225 2.87669 18.0805 3.1255L21.059 5.9975C21.1916 6.12538 21.2973 6.27848 21.3698 6.44778C21.4424 6.61708 21.4803 6.79918 21.4815 6.98337C21.4827 7.16756 21.447 7.35012 21.3766 7.52033C21.3062 7.69053 21.2025 7.84495 21.0715 7.9745L7.63704 21.2615ZM3.47254 20.3385H7.25854L20.4235 7.3195C20.4672 7.27634 20.5018 7.22488 20.5253 7.16815C20.5488 7.11142 20.5607 7.05056 20.5603 6.98916C20.56 6.92776 20.5473 6.86705 20.5231 6.81061C20.499 6.75416 20.4637 6.70313 20.4195 6.6605L17.441 3.7885C17.3552 3.70538 17.2403 3.65891 17.1208 3.65891C17.0013 3.65891 16.8864 3.70538 16.8005 3.7885L3.60054 16.5885L3.47254 20.3385Z"
                          fill="#707070"
                        />
                        <path
                          className="edit-icon-path"
                          strokeWidth="1.5"
                          d="M18.8589 9.5245L14.4719 5.21L15.2029 4.491L19.5899 8.8055L18.8589 9.5245Z"
                          fill="#707070"
                        />
                      </svg>
                    </div>
                  ) : (
                    iconPlaceholder
                  )}
                </div>
              </Flexbox>
            </div>

            <div>
              <Divider className="divider-line push-20-b" />
              <Flexbox justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
                <div style={{ flex: 1 }}>
                  <div className="bp3-text-large " style={{ fontWeight: "500", fontSize: "14px" }}>
                    Enable Checkin for Goals
                  </div>
                  <div className="bp3-text-muted" style={{ fontSize: "14px", marginTop: "3px" }}>
                    Provides the ability for admins to create and manage checkins for goals.
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center"}}>
                  <Switch checked={isCheckinEnabled} onChange={() => setIsCheckinEnabled(!isCheckinEnabled)} />
                  {isCheckinEnabled ? (
                    <Tooltip portalContainer={document.body}
                      content="Manage checkins"
                      position={Position.RIGHT}
                      hoverOpenDelay={100}
                      boundary="viewport"
                      className="custom-bp-tooltip"
                    >
                       <div
                        className="edit-icon"
                        onClick={handleManageCheckin}
                        style={{ cursor: "pointer" }}

                      >
                        <svg
                          width="21"
                          height="21"
                          viewBox="0 0 24 24"
                          fill="none"
                          strokeWidth="1.5"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            className="edit-icon-path"
                            strokeWidth="1.5"
                            d="M7.63704 21.2615H2.51904L2.69554 16.1885L16.1595 3.1285C16.4168 2.87888 16.761 2.73903 17.1194 2.73847C17.4779 2.73791 17.8225 2.87669 18.0805 3.1255L21.059 5.9975C21.1916 6.12538 21.2973 6.27848 21.3698 6.44778C21.4424 6.61708 21.4803 6.79918 21.4815 6.98337C21.4827 7.16756 21.447 7.35012 21.3766 7.52033C21.3062 7.69053 21.2025 7.84495 21.0715 7.9745L7.63704 21.2615ZM3.47254 20.3385H7.25854L20.4235 7.3195C20.4672 7.27634 20.5018 7.22488 20.5253 7.16815C20.5488 7.11142 20.5607 7.05056 20.5603 6.98916C20.56 6.92776 20.5473 6.86705 20.5231 6.81061C20.499 6.75416 20.4637 6.70313 20.4195 6.6605L17.441 3.7885C17.3552 3.70538 17.2403 3.65891 17.1208 3.65891C17.0013 3.65891 16.8864 3.70538 16.8005 3.7885L3.60054 16.5885L3.47254 20.3385Z"
                            fill="#707070"
                          />
                          <path
                            className="edit-icon-path"
                            strokeWidth="1.5"
                            d="M18.8589 9.5245L14.4719 5.21L15.2029 4.491L19.5899 8.8055L18.8589 9.5245Z"
                            fill="#707070"
                          />
                        </svg>
                      </div>
                      
                    </Tooltip>
                  ) : (
                    iconPlaceholder
                  )}
                </div>
              </Flexbox>
            </div>

            <div>
              <Divider className="divider-line push-20-b" />
              <Flexbox justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
                <div style={{ flex: 1 }}>
                  <div className="bp3-text-large " style={{ fontWeight: "500", fontSize: "14px" }}>
                    Enable Rating for Assessment and Review Questions
                  </div>
                  <div className="bp3-text-muted" style={{ fontSize: "14px", marginTop: "3px" }}>
                    Provides a rating system for assessment and review questions.
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Switch checked={isRatingEnabledQuestions} onChange={() => (!isRatingEnabledQuestions ? setIsRatingEnabledQuestions(true) : setIsRatingEnabledQuestions(false))} />
                  {iconPlaceholder}
                </div>
              </Flexbox>
            </div>

            <div>
              <Divider className="divider-line push-20-b" />
              <Flexbox justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
                <div style={{ flex: 1 }}>
                  <div className="bp3-text-large " style={{ fontWeight: "500", fontSize: "14px" }}>
                    Add Custom Status
                  </div>
                  <div className="bp3-text-muted" style={{ fontSize: "14px", marginTop: "3px" }}>
                    Provides the ability for admins to create and manage custom statuses for progress tracking.
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Switch
                    readOnly checked={true}

                  />
                  {iconPlaceholder}
                </div>
              </Flexbox>
            </div>
            <GoalProgressStatusManager ref={statusRef} goalSettingId={goalDataId} />

          </Flexbox>

          {error && (
            <p intent="danger" style={{ color: "red" }}>
              {error}
            </p>
          )}
          <Flexbox flexGrow={1} justifyContent="flex-start" className="push-20-t" marginBottom="10px">
            <div>
            {(goalData?.id == null || status == 0) && (
                <OutlinedButton text={"Save as draft testing"} type="submit" onClick={e => handleSubmit(e, false)} style={{ marginRight: "10px" }} />
              )}
              <FilledButton text={ "Save as Published"} type="submit" onClick={e => handleSubmit(e, true)} disabled={goalSubmitLoading} />
            </div>
          </Flexbox>

          {isAssesementDrawerOpen && (
            <AssesementQuestionDrawer
              isOpen={isAssesementDrawerOpen}
              onClose={() => setIsAssesementDrawerOpen(false)}
              goalId={currentGoalData?.id}
              assessmentSettingId={assessmentSettingId}
              isWeightAllow={weight}
            />
          )}
          {isManagerDrawerOpen && (
            <ManagerQuestionDrawer
              isOpen={isManagerDrawerOpen}
              onClose={() => setIsManagerDrawerOpen(false)}
              goalId={goalDataId}
              isWeightAllow={weight}
            />
          )}
          {isRatingLabelDialogOpen && (
            <RatingLabelDialog
              isOpen={isRatingLabelDialogOpen}
              onClose={() => setIsRatingLabelDialogOpen(false)}
              onSave={(updated) => {
                setRatingLabels(updated); // Save locally in parent
              }}
              startRange={startRatingValue}
              endRange={endRatingValue}
              existingLabels={ratingLabels || []}
            />
          )}
          {isMultilevelSettingDialogOpen && (
            <MultilevelSettingDialog
              isOpen={isMultilevelSettingDialogOpen}
              onClose={() => setIsMultilevelSettingDialogOpen(false)}
              defaultRatingMethod={ratingMethod}
              onSaveRatingMethod={(method) => {
                setRatingMethod(method);
                ToastStore.showToast("Rating method added", "success");
                setIsMultilevelSettingDialogOpen(false);
              }}
              globalReviewSteps={globalReviewSteps}
              onSaveReviewSteps={(steps) => {
                setGlobalReviewSteps(steps);
                ToastStore.showToast("Review steps added", "success");
                setIsMultilevelSettingDialogOpen(false);
              }}
            />
          )}
          {isStepProcessDrawerOpen && (
            <StepProcessDrawer
              isOpen={isStepProcessDrawerOpen}
              onClose={() => setIsStepProcessDrawerOpen(false)}
              goalCycleId={currentGoalData?.id || goalDataId}
              isWeightAllow={weight}
            />
          )}
          {isCheckinDialogOpen && (
            <CheckInFrequencySettingDialog
              isOpen={isCheckinDialogOpen}
              onClose={() => setIsCheckinDialogOpen(false)}
              goalCycleId={currentGoalData?.id || goalDataId}
              checkinFrequency={checkinFrequency}
              setCheckinFrequency={setCheckinFrequency}
              goalDuration={reviewPeriod}
              goalFrequency={reviewPeriodFrequency}
              onSaveCheckinFrequency={(frequency) => {
                setCheckinFrequency(frequency);
                ToastStore.showToast("Check-in frequency added", "success");
                setIsCheckinDialogOpen(false);
              }}
              onCancel={() =>  {
                setCheckinFrequency("");
                setIsCheckinDialogOpen(false);
              }}
            />
          )}
        </form>
      </Flexbox>
    );
  })
);

export default GoalSettingsCreateForm;


